import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { fetchHotels, fetchAvailableHotels, recordSponsoredClick } from "@/lib/api";
import { OffreLancementTeaser } from "@/components/OffreLancementTeaser";
import { fmtPrice, withWatermark } from "@/lib/utils";
import { MapPin, Search, RotateCcw, BedDouble, Loader2, BadgeCheck, Megaphone, Gift } from "lucide-react";

const pad = (n) => String(n).padStart(2, "0");
const HOURS = Array.from({ length: 24 }, (_, i) => i);

export default function HotelleriesPage() {
  const [hotels, setHotels] = useState([]);
  const today = new Date().toISOString().slice(0, 10);
  const [date, setDate] = useState(today);
  const [startHour, setStartHour] = useState(8);
  const [endHour, setEndHour] = useState(20);
  const [searching, setSearching] = useState(false);
  const [searchResult, setSearchResult] = useState(null);

  useEffect(() => { fetchHotels().then(setHotels); }, []);

  const runSearch = async () => {
    setSearching(true);
    try {
      const res = await fetchAvailableHotels({ date, start_hour: startHour, end_hour: endHour });
      setSearchResult(res);
    } catch {
      setSearchResult(null);
    } finally { setSearching(false); }
  };

  const resetSearch = () => { setSearchResult(null); };

  const displayed = searchResult ? searchResult.hotels : hotels;
  const availInfo = searchResult
    ? Object.fromEntries(searchResult.hotels.map((h) => [h.id, h]))
    : {};

  return (
    <main className="max-w-7xl mx-auto px-6 py-10" data-testid="hotelleries-page">
      <h1 className="font-display text-4xl md:text-5xl font-extrabold tracking-tight">Hotelleries partenaires</h1>
      <p className="text-[var(--ink-soft)] mt-2">Chambres libres a prix competitifs avec remises intelligentes selon les heures creuses et la forte affluence.</p>

      {/* Recherche par plage horaire */}
      <Link to="/hotelleries/offre-lancement" className="mt-6 flex flex-wrap items-center justify-between gap-3 rounded-2xl bg-[#0f1a2b] text-white px-5 py-4 hover:bg-[#16243a]" data-testid="offre-lancement-banner">
        <span className="inline-flex items-center gap-2 text-sm font-bold"><Gift size={16} className="text-amber-300" /> Vous gerez un hotel ? 30 jours Pro Hotel offerts aux 20 premiers inscrits <OffreLancementTeaser compact /></span>
        <span className="text-xs font-bold uppercase tracking-widest text-amber-300">Reserver ma place →</span>
      </Link>
      <section className="mt-8 glass-card p-5" data-testid="hotel-search-bar">
        <div className="font-bold text-sm mb-3 inline-flex items-center gap-2">
          <BedDouble size={16} className="text-orange-500" /> Trouver un hotel disponible
        </div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 items-end">
          <div className="col-span-2 md:col-span-1">
            <label className="text-xs font-semibold text-[var(--ink-soft)]">Date</label>
            <input
              type="date"
              min={today}
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="w-full mt-1 !py-2 !px-3 rounded-xl border border-[var(--line)] bg-white text-sm"
              data-testid="hotel-search-date"
            />
          </div>
          <div>
            <label className="text-xs font-semibold text-[var(--ink-soft)]">De</label>
            <select
              value={startHour}
              onChange={(e) => setStartHour(Number(e.target.value))}
              className="w-full mt-1 py-2 px-3 rounded-xl border border-[var(--line)] bg-white text-sm"
              data-testid="hotel-search-start-hour"
            >
              {HOURS.map((h) => <option key={h} value={h}>{pad(h)}:00</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-semibold text-[var(--ink-soft)]">A</label>
            <select
              value={endHour}
              onChange={(e) => setEndHour(Number(e.target.value))}
              className="w-full mt-1 py-2 px-3 rounded-xl border border-[var(--line)] bg-white text-sm"
              data-testid="hotel-search-end-hour"
            >
              {HOURS.map((h) => <option key={h} value={h}>{pad(h)}:00</option>)}
            </select>
          </div>
          <button
            type="button"
            onClick={runSearch}
            disabled={searching}
            className="btn-primary inline-flex items-center justify-center gap-2 disabled:opacity-60"
            data-testid="hotel-search-btn"
          >
            {searching ? <Loader2 size={15} className="animate-spin" /> : <Search size={15} />}
            Rechercher
          </button>
          {searchResult && (
            <button
              type="button"
              onClick={resetSearch}
              className="btn-secondary inline-flex items-center justify-center gap-2"
              data-testid="hotel-search-reset"
            >
              <RotateCcw size={14} /> Tout afficher
            </button>
          )}
        </div>
        {searchResult && (
          <div className="mt-3 text-sm font-semibold" data-testid="hotel-search-result-count">
            {searchResult.count === 0
              ? <span className="text-red-600">Aucun hotel disponible sur cette plage. Essayez d&apos;autres horaires.</span>
              : <span className="text-emerald-700">{searchResult.count} hotel{searchResult.count > 1 ? "s" : ""} disponible{searchResult.count > 1 ? "s" : ""} le {searchResult.date} entre {pad(searchResult.start_hour)}:00 et {pad(searchResult.end_hour)}:00</span>}
          </div>
        )}
      </section>

      <div className="mt-8 rounded-[28px] overflow-hidden bg-gradient-to-br from-orange-500 to-orange-400 text-white p-8 md:p-10 flex flex-col md:flex-row gap-6 md:items-center justify-between">
        <div>
          <div className="text-xs uppercase tracking-widest opacity-90">Programme partenaire</div>
          <h2 className="font-display text-2xl md:text-3xl font-extrabold mt-2">Maximiser le taux d'occupation des hotels d'Abidjan</h2>
          <p className="opacity-95 mt-2 max-w-xl">Remise de 40% en heures creuses et 20% en forte affluence pour rester competitif et combler les chambres vides.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Link to="/hotelleries/h1" className="btn-secondary">Voir une fiche hotel</Link>
          <Link to="/deposer" className="btn-secondary">Referencer un hotel</Link>
        </div>
      </div>

      <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {displayed.map((h, i) => {
          const info = availInfo[h.id];
          return (
            <Link key={h.id} to={`/hotelleries/${h.id}`} onClick={() => h.sponsored && h.campaign_id && recordSponsoredClick(h.campaign_id)} className={`card rise ${h.sponsored ? "ring-2 ring-violet-400" : ""}`} style={{ animationDelay: `${i * 0.04}s` }} data-testid={`hotel-card-${h.id}`}>
              <div className="relative">
                <img src={withWatermark(h.image)} alt={h.name} className="w-full h-48 object-cover object-bottom" />
                {h.tag && <span className="tag">{h.tag}</span>}
                {h.sponsored && (
                  <span className="absolute top-2 left-2 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest bg-violet-600 text-white px-2 py-1 rounded-full shadow" data-testid={`hotel-sponsored-badge-${h.id}`}>
                    <Megaphone size={11} /> Sponsorise
                  </span>
                )}
                {h.is_partner && (
                  <span className="absolute top-2 right-2 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest bg-amber-400 text-amber-950 px-2 py-1 rounded-full shadow" data-testid={`hotel-partner-badge-${h.id}`}>
                    <BadgeCheck size={11} /> Partenaire
                  </span>
                )}
                {info && (
                  <span className="absolute bottom-2 left-2 text-[10px] font-bold uppercase tracking-widest bg-emerald-500 text-white px-2 py-1 rounded-full" data-testid={`hotel-available-badge-${h.id}`}>
                    {info.available_rooms_total} chambres dispo
                  </span>
                )}
              </div>
              <div className="p-4">
                <h3 className="font-bold text-[15px]">{h.name}</h3>
                {h.rating_count > 0 && <div className="text-xs mt-0.5 inline-flex items-center gap-1" data-testid={`hotel-rating-${h.id}`}><span className="text-amber-500">★</span> <b>{h.rating_avg}</b> · {h.rating_count} avis verifies</div>}
                <div className="text-orange-600 font-bold mt-1">
                  {fmtPrice(info?.min_price_in_range ?? h.price)}
                  <span className="text-xs text-[var(--ink-soft)] font-medium"> / nuit{info ? " (des)" : ""}</span>
                </div>
                <div className="mt-2 flex items-center gap-1 text-xs text-[var(--ink-soft)]">
                  <MapPin size={12} /> {h.city}{h.quartier ? ` - ${h.quartier}` : ""}
                </div>
              </div>
            </Link>
          );
        })}
      </div>
    </main>
  );
}
